<template>
  <v-dialog
    data-cy="image-picker-modal"
    :model-value="modelValue"
    scrollable
    class="modal"
    :fullscreen="false"
    :transition="isFullscreen ? 'dialog-bottom-transition' : 'dialog-transition'"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card>
      <!-- Header -->
      <v-card-title
        data-cy="modal-header"
        class="d-flex justify-space-between align-center pa-4 pb-2"
      >
        <span data-cy="modal-title" class="modal-title">
          {{ t('components.imagePickerSimplified.title') }}
        </span>

        <v-btn
          data-cy="close-button"
          icon
          variant="text"
          @click="$emit('update:modelValue', false)"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <!-- Subtitle and image sections -->
      <v-card-text class="pa-4 modal-card-text">
        <p data-cy="modal-subtitle" class="modal-subtitle mb-8">
          {{ t('components.imagePickerSimplified.subtitle') }}
        </p>
        <div
          v-for="(categoryImages, categoryName) in groupedImages"
          :key="categoryName"
          class="category-section mb-6"
          :data-cy="`category-${categoryName}`"
        >
          <h3
            class="category-title mb-3"
            :data-cy="`category-title-${categoryName}`"
          >
            {{ categoryLabel(categoryName) }}
          </h3>

          <div
            class="image-grid"
            :data-cy="`image-grid-${categoryName}`"
          >
            <div
              v-for="image in categoryImages"
              :key="image.id"
              class="image-item-wrapper"
              :data-cy="`image-item-${image.id}`"
              @click="selectImage(image)"
            >
              <div
                v-if="!isImageLoaded(image.id)"
                class="image-item-loader"
                data-cy="image-loader"
              >
                <v-progress-circular
                  indeterminate
                  color="primary"
                  :size="24"
                  :width="2"
                />
              </div>
              <img
                :src="getFullUrl(image.url)"
                class="image-item"
                :class="{ 'image-item--loaded': isImageLoaded(image.id) }"
                alt=""
                @load="onImageLoad(image.id)"
                @error="onImageLoad(image.id)"
              />
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { useLabel } from '@/composables/useLabel';

export default defineComponent({
  name: 'ImagePickerModalSimplified',
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    images: {
      type: Array,
      required: true,
    },
  },
  emits: ['update:modelValue', 'imageSelected'],
  setup(props, { emit }) {
    const { t } = useLabel();

    const isFullscreen = ref(false);
    const loadedImageIds = ref<Record<number, boolean>>({});

    const updateFullscreen = () => {
      isFullscreen.value = window.innerWidth <= 430;
    };

    onMounted(() => {
      updateFullscreen();
      window.addEventListener('resize', updateFullscreen);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', updateFullscreen);
    });

    const groupedImages = computed(() => {
      const groups: Record<string, any[]> = {};

      (props.images || []).forEach((img: any) => {
        const category = img.donationImageCategory?.name || 'Other';
        if (!groups[category]) {
          groups[category] = [];
        }
        groups[category].push(img);
      });

      return groups;
    });

    // const getFullUrl = (path: string) => {
    //   const base = import.meta.env.VITE_API_BASE_URL || '';
    //   return path.startsWith('http') ? path : `${base}${path}`;
    // };
    const getFullUrl = (path: string) => {
      const base =
        (typeof window !== 'undefined' &&
          (window as any).__VITE_ENV__?.VITE_API_BASE_URL) ||
        '';

      return path.startsWith('http') ? path : `${base}${path}`;
    };

    const categoryLabel = (categoryName: string) => {
      const key = `components.imagePickerSimplified.${categoryName.toLowerCase()}`;
      return t(key) || categoryName;
    };

    const isImageLoaded = (imageId: number) => !!loadedImageIds.value[imageId];

    const onImageLoad = (imageId: number) => {
      loadedImageIds.value = {
        ...loadedImageIds.value,
        [imageId]: true,
      };
    };

    watch(
      () => props.images,
      () => {
        loadedImageIds.value = {};
      }
    );

    const selectImage = (image: any) => {
      emit('imageSelected', image.id);
      emit('update:modelValue', false);
    };

    return {
      t,
      isFullscreen,
      groupedImages,
      categoryLabel,
      selectImage,
      getFullUrl,
      isImageLoaded,
      onImageLoad,
    };
  },
});
</script>

<style scoped lang="scss">

:deep(.v-overlay__content) {
  width: auto !important;
  
  @media (max-width: 430px) {
    position: absolute !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    height: 90vh !important;
    width: 100% !important;
    max-width: none !important;
  }
}

@media (max-width: 430px) {
  :deep(.v-overlay) {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
  }
  :deep(.v-overlay__content) {
    margin: 0px !important;
  }
}



:deep(.v-card.v-theme--light.v-card--density-default.v-card--variant-elevated) {
  border-bottom-right-radius: 0px !important;
  border-bottom-left-radius: 0px !important;
}

:deep(.v-card.v-theme--light.v-card--density-default.v-card--variant-elevated) {
  @media (min-width: 430px) {
    border-radius: 16px !important;
  }
}

.modal {
  max-width: 430px !important;
}

.modal-title {
  font-family: 'Lato', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #1F2A37;
  line-height: 1.2;
}

.modal-subtitle {
  font-family: 'Lato', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #6B7280;
  line-height: 1.5;
}

.category-title {
  color: #374351;
  font-weight: 600;
  font-size: 16px;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
}

.image-item-wrapper {
  position: relative;
  width: 112px;
  height: 112px;
  cursor: pointer;
  background-color: #f3f4f6;
  overflow: hidden;

  @media (max-width: 430px) {
    width: 100%;
    height: auto;
    aspect-ratio: 1;
  }
}

.image-item-loader {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.image-item {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  display: block;
  transition: opacity 0.15s ease-in;

  &--loaded {
    opacity: 1;
  }
}

.v-overlay--scroll-blocked {
  margin-top: 0px !important;
}

</style>
